/**
 * @fileoverview Template tag parser for Text component
 * @module screen-view-v2/utils/templateTagParser
 *
 * Parses template text with:
 * - Label tags: <tagName>styled content</tagName> → Apply styles from parameters.labels
 * - Link references: @linkName → Resolve from parameters.links (URL or screen value)
 *
 * Regex patterns match the editor serialization format in:
 * src/modules/reactFlow/components/shared/templateFields/TiptapTemplateEditor/utils/serialization.ts
 */

import type { TypographyStyle } from '@segmently/schema-models/v2';
import type { InlineCountdownConfig } from '@segmently/schema-models/v2';

// ============= Types =============

/** Plain text segment */
export interface TextSegment {
  type: 'text';
  content: string;
}

/** Styled text segment with TypographyStyle styles */
export interface LabelSegment {
  type: 'label';
  tagName: string;
  content: string;
  styles: TypographyStyle;
}

/** Link segment - either external URL or resolved screen value */
export interface LinkSegment {
  type: 'link';
  linkName: string;
  /** For external: URL, for screen: resolved value or empty string */
  href: string;
  /** True if href is an external URL (clickable) */
  isExternal: boolean;
  /** Optional label styles when link is wrapped in <tag>@link</tag> */
  labelStyles?: TypographyStyle;
  /** The raw links[name] value (URL, site path, fragment or screen id); article links read it (G2, D8). */
  value: string;
}

/** Countdown segment - inline ticking timer */
export interface CountdownSegment {
  type: 'countdown';
  timerName: string;
  config: InlineCountdownConfig;
  /** Optional label styles when countdown is wrapped in <tag>@timer</tag> */
  labelStyles?: TypographyStyle;
}

/** Union of all segment types */
export type ParsedSegment = TextSegment | LabelSegment | LinkSegment | CountdownSegment;

/** Result of parsing template text */
export interface ParseResult {
  segments: ParsedSegment[];
  hasLabels: boolean;
  hasLinks: boolean;
  hasCountdowns: boolean;
}

// ============= Regex Patterns =============

/**
 * Matches <tagName>content</tagName> patterns
 * Uses [^\s<>/]+ to allow tag names with special characters (e.g., apostrophes)
 * Note: Must stay in sync with editor serialization.ts
 */
const TAG_PATTERN = /<([^\s<>/]+)>([\s\S]*?)<\/\1>/g;

/**
 * Matches @linkName patterns
 * Uses [^\s@<>]+ to allow link names with special characters
 * Note: Must stay in sync with editor serialization.ts
 */
const LINK_PATTERN = /@([^\s@<>]+)/g;

// ============= Internal Types =============

interface MatchInfo {
  type: 'label' | 'link' | 'countdown';
  start: number;
  end: number;
  tagName: string;
  content?: string; // Only for labels
}

// ============= Main Function =============

/**
 * Parse template text and return segments for rendering
 *
 * Processing order:
 * 1. Find all <tag>content</tag> patterns
 * 2. Find all @linkName patterns
 * 3. Sort by position, fill gaps with text segments
 *
 * @param text - Text content (after l10n resolution and @variable substitution)
 * @param labels - Label styles dictionary from parameters.labels
 * @param links - Links dictionary from parameters.links (URL or screenId)
 * @param resolvedScreenValues - Pre-resolved screen values from FunnelEngine
 * @returns ParseResult with segments array
 *
 * @example
 * parseTemplateTags(
 *   'Subscribe for <highlight>$9.99</highlight> - read @terms',
 *   { highlight: { color: '#FF0000' } },
 *   { terms: 'https://example.com/terms' },
 *   {}
 * );
 */
export function parseTemplateTags(
  text: string,
  labels: Record<string, TypographyStyle> = {},
  links: Record<string, string> = {},
  resolvedScreenValues: Record<string, string> = {},
  countdowns: Record<string, InlineCountdownConfig> = {}
): ParseResult {
  // Fast path: empty or null text
  if (!text) {
    return { segments: [], hasLabels: false, hasLinks: false, hasCountdowns: false };
  }

  const matches: MatchInfo[] = [];

  // Find all label tags <tagName>content</tagName>
  const tagRegex = new RegExp(TAG_PATTERN.source, 'g');
  let match: RegExpExecArray | null;

  while ((match = tagRegex.exec(text)) !== null) {
    matches.push({
      type: 'label',
      start: match.index,
      end: match.index + match[0].length,
      tagName: match[1],
      content: match[2],
    });
  }

  // Find all @name references — disambiguate countdown vs link
  // Priority: countdown > link (same as editor serialization.ts)
  const linkRegex = new RegExp(LINK_PATTERN.source, 'g');

  while ((match = linkRegex.exec(text)) !== null) {
    // Check if this position overlaps with an existing label match
    const overlaps = matches.some(
      (m) => match!.index >= m.start && match!.index < m.end
    );

    if (!overlaps) {
      const mentionName = match[1];
      if (mentionName in countdowns) {
        // Countdown takes priority over link
        matches.push({
          type: 'countdown',
          start: match.index,
          end: match.index + match[0].length,
          tagName: mentionName,
        });
      } else {
        matches.push({
          type: 'link',
          start: match.index,
          end: match.index + match[0].length,
          tagName: mentionName,
        });
      }
    }
  }

  // Fast path: no matches found
  if (matches.length === 0) {
    return {
      segments: [{ type: 'text', content: text }],
      hasLabels: false,
      hasLinks: false,
      hasCountdowns: false,
    };
  }

  // Sort by position
  matches.sort((a, b) => a.start - b.start);

  // Build segments array
  const segments: ParsedSegment[] = [];
  let lastIndex = 0;
  let hasLabels = false;
  let hasLinks = false;
  let hasCountdowns = false;

  for (const m of matches) {
    // Add text before this match
    if (m.start > lastIndex) {
      const textBefore = text.slice(lastIndex, m.start);
      if (textBefore) {
        segments.push({ type: 'text', content: textBefore });
      }
    }

    if (m.type === 'label') {
      hasLabels = true;
      const styles = labels[m.tagName];
      const innerContent = m.content || '';

      // Check if label wraps a single mention: <tag>@mentionName</tag>
      const innerMentionMatch = innerContent.match(/^@([^\s@<>]+)$/);
      if (innerMentionMatch) {
        const mentionName = innerMentionMatch[1];
        if (mentionName in countdowns) {
          // Labeled countdown: <tag>@timer</tag>
          hasCountdowns = true;
          const config = countdowns[mentionName];
          if (config) {
            segments.push({
              type: 'countdown',
              timerName: mentionName,
              config,
              labelStyles: styles,
            });
          } else {
            segments.push({ type: 'text', content: innerContent });
          }
        } else if (mentionName in links) {
          // Labeled link: <tag>@link</tag>
          hasLinks = true;
          const linkValue = links[mentionName];
          const isExternal = isExternalUrl(linkValue);
          if (isExternal) {
            segments.push({
              type: 'link',
              linkName: mentionName,
              href: linkValue,
              isExternal: true,
              labelStyles: styles,
              value: linkValue,
            });
          } else {
            const resolvedValue = resolvedScreenValues[mentionName] || '';
            segments.push({
              type: 'link',
              linkName: mentionName,
              href: resolvedValue,
              isExternal: false,
              labelStyles: styles,
              value: linkValue,
            });
          }
        } else {
          // Unknown mention inside label — render as styled text
          if (styles) {
            segments.push({ type: 'label', tagName: m.tagName, content: innerContent, styles });
          } else {
            segments.push({ type: 'text', content: innerContent });
          }
        }
      } else if (styles) {
        // Known label with styles (regular text content)
        segments.push({
          type: 'label',
          tagName: m.tagName,
          content: innerContent,
          styles,
        });
      } else {
        // Unknown label - render content as plain text (graceful fallback)
        segments.push({
          type: 'text',
          content: innerContent,
        });
      }
    } else if (m.type === 'countdown') {
      hasCountdowns = true;
      const config = countdowns[m.tagName];

      if (config) {
        segments.push({
          type: 'countdown',
          timerName: m.tagName,
          config,
        });
      } else {
        // Unknown countdown - render @timerName as plain text
        segments.push({
          type: 'text',
          content: `@${m.tagName}`,
        });
      }
    } else if (m.type === 'link') {
      hasLinks = true;
      const linkValue = links[m.tagName];

      if (linkValue) {
        const isExternal = isExternalUrl(linkValue);

        if (isExternal) {
          // External URL - clickable
          segments.push({
            type: 'link',
            linkName: m.tagName,
            href: linkValue,
            isExternal: true,
            value: linkValue,
          });
        } else {
          // Screen reference - use resolved value
          const resolvedValue = resolvedScreenValues[m.tagName] || '';
          segments.push({
            type: 'link',
            linkName: m.tagName,
            href: resolvedValue,
            isExternal: false,
            value: linkValue,
          });
        }
      } else {
        // Unknown link - render @linkName as plain text
        segments.push({
          type: 'text',
          content: `@${m.tagName}`,
        });
      }
    }

    lastIndex = m.end;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    if (remainingText) {
      segments.push({ type: 'text', content: remainingText });
    }
  }

  return { segments, hasLabels, hasLinks, hasCountdowns };
}

/**
 * Check if a link value is an external URL
 */
function isExternalUrl(value: string): boolean {
  return value.startsWith('http://') || value.startsWith('https://');
}

/**
 * Check if text contains any template tags or links
 * Useful for optimization - skip parsing if no patterns found
 */
export function hasTemplateTags(text: string): boolean {
  if (!text) return false;
  return TAG_PATTERN.test(text) || LINK_PATTERN.test(text);
}
